import { useEffect, useState, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';
import { useGame, ACTIONS } from '../context/GameContext';
import { useTimer } from '../hooks/useTimer';
import { wordService } from '../services/wordService';
import { statsService } from '../services/statsService';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GameHUD } from '../components/game/GameHUD';
import { WordDisplay } from '../components/game/WordDisplay';
import { OutcomePanel } from '../components/game/OutcomePanel';
import { EndScreen } from '../components/game/EndScreen';
import { useSoundEngine } from '../hooks/useSoundEngine';
import './LocalGame.css';

const TOTAL_TIME = 60;
const COUNTDOWN_FROM = 3;

const LocalGame = () => {
  const { state, dispatch } = useGame();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const sound = useSoundEngine();

  const [countdown, setCountdown] = useState(COUNTDOWN_FROM);
  const [isLoading, setIsLoading] = useState(true);
  const [fastestWordMs, setFastestWordMs] = useState(null);
  const [wordStartTimeLeft, setWordStartTimeLeft] = useState(TOTAL_TIME);
  const lastWordStartTime = useRef(null);

  const handleTimeUp = useCallback(() => {
    dispatch({ type: ACTIONS.PAUSE_GAME });
  }, [dispatch]);

  const { timeLeft, isRunning, start, pause, reset } = useTimer(TOTAL_TIME, {
    onTimeUp: handleTimeUp,
  });

  // Load words on mount (reset game state)
  useEffect(() => {
    dispatch({ type: ACTIONS.RESET });
    dispatch({ type: ACTIONS.SET_MODE, payload: 'local' });
    setIsLoading(true);
    wordService.getWords(80).then((words) => {
      dispatch({ type: ACTIONS.SET_WORDS, payload: words });
      setIsLoading(false);
    });

    return () => {
      dispatch({ type: ACTIONS.RESET });
    };
  }, [dispatch]);

  // Countdown effect
  useEffect(() => {
    if (state.status !== 'countdown') return;
    setCountdown(COUNTDOWN_FROM);
    sound.playCountdownBeep(COUNTDOWN_FROM);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          dispatch({ type: ACTIONS.START_GAME });
          // Start timing the first word
          lastWordStartTime.current = Date.now();
          return prev;
        }
        const next = prev - 1;
        sound.playCountdownBeep(next);
        return next;
      });
    }, 900);
    return () => clearInterval(interval);
  }, [state.status, dispatch, sound]);

  // Sync timer with game state
  useEffect(() => {
    if (state.status === 'playing' && !isRunning) start();
    if (state.status === 'paused' && isRunning) pause();
    if (state.status === 'finished') pause();
  }, [state.status, isRunning, start, pause]);

  // Suono nuova parola — si attiva ad ogni cambio di parola durante il gioco
  const prevWordIndex = useRef(-1);
  useEffect(() => {
    if (state.status === 'playing' && state.currentWordIndex !== prevWordIndex.current) {
      if (prevWordIndex.current !== -1) sound.playNewWord();
      prevWordIndex.current = state.currentWordIndex;
    }
    if (state.status !== 'playing') prevWordIndex.current = -1;
  }, [state.currentWordIndex, state.status, sound]);

  // Save stats + confetti + fanfare on game end
  const prevStatus = useRef(state.status);
  useEffect(() => {
    if (state.status === 'finished' && prevStatus.current !== 'finished') {
      if (currentUser) {
        statsService
          .saveGameResult(currentUser.uid, {
            score: state.score,
            wordsGuessed: state.wordsGuessed,
            wrongAnswers: state.wrongAnswers,
            passesUsed: 3 - state.passesLeft,
            fastestWordMs: fastestWordMs,
            wordsHistory: state.wordsHistory || [],
            mode: 'local',
          })
          .catch(console.error);
      }
      sound.playGameEnd();
      confetti({
        particleCount: 140,
        spread: 100,
        origin: { y: 0.55 },
        colors: ['#FFCC00', '#FF8800', '#FF4433', '#22C55E'],
      });
    }
    prevStatus.current = state.status;
  }, [
    state.status,
    currentUser,
    state.score,
    state.wordsGuessed,
    state.wrongAnswers,
    state.passesLeft,
    state.wordsHistory,
    fastestWordMs,
    sound,
  ]);

  const currentWordObj = state.words[state.currentWordIndex];
  const currentWordText = currentWordObj?.text || '...';

  const handleStartCountdown = () => {
    reset(TOTAL_TIME);
    setFastestWordMs(null);
    setWordStartTimeLeft(TOTAL_TIME);
    dispatch({ type: ACTIONS.COUNTDOWN });
  };

  const handleStop = () => {
    if (lastWordStartTime.current) {
      const elapsed = Date.now() - lastWordStartTime.current;
      if (fastestWordMs === null || elapsed < fastestWordMs) {
        setFastestWordMs(elapsed);
      }
    }
    sound.playStop();
    dispatch({ type: ACTIONS.PAUSE_GAME });
    pause();
  };

  const handleCorrect = () => {
    const elapsed = (Date.now() - lastWordStartTime.current) / 1000;
    if (currentWordObj?.id) {
      wordService.updateWordStats(currentWordObj.id, 'correct', elapsed);
    }
    sound.playCorrect();
    dispatch({ type: ACTIONS.CORRECT_GUESS });
    if (timeLeft <= 0) {
      dispatch({ type: ACTIONS.END_GAME });
    } else {
      dispatch({ type: ACTIONS.RESUME_GAME });
      lastWordStartTime.current = Date.now();
      setWordStartTimeLeft(timeLeft);
    }
  };

  const handleWrong = () => {
    if (currentWordObj?.id) {
      wordService.updateWordStats(currentWordObj.id, 'wrong');
    }
    sound.playWrong();
    dispatch({ type: ACTIONS.WRONG_GUESS });
    if (timeLeft <= 0) {
      dispatch({ type: ACTIONS.END_GAME });
    } else {
      dispatch({ type: ACTIONS.RESUME_GAME });
      lastWordStartTime.current = Date.now();
      setWordStartTimeLeft(timeLeft);
    }
  };

  const handlePass = () => {
    if (timeLeft <= 0) {
      dispatch({ type: ACTIONS.END_GAME });
      return;
    }
    if (state.passesLeft <= 0) return;
    if (currentWordObj?.id) {
      wordService.updateWordStats(currentWordObj.id, 'pass');
    }
    sound.playNewWord();
    dispatch({ type: ACTIONS.PASS_WORD });
    if (timeLeft <= 0) {
      dispatch({ type: ACTIONS.END_GAME });
    } else {
      dispatch({ type: ACTIONS.RESUME_GAME });
      lastWordStartTime.current = Date.now();
      setWordStartTimeLeft(timeLeft);
    }
  };

  const handleReport = () => {
    if (currentWordObj?.id) {
      wordService.reportWord(currentWordObj.id);
    }
    // Restore time
    reset(wordStartTimeLeft);
    dispatch({ type: ACTIONS.REPORTED_WORD });
    lastWordStartTime.current = Date.now();
  };

  const handleRestart = () => {
    setIsLoading(true);
    reset(TOTAL_TIME);
    dispatch({ type: ACTIONS.RESET });
    wordService.getWords(80).then((words) => {
      dispatch({ type: ACTIONS.SET_WORDS, payload: words });
      setIsLoading(false);
      dispatch({ type: ACTIONS.COUNTDOWN });
    });
  };

  const handleExit = () => {
    if (state.status === 'idle' || state.status === 'finished') {
      navigate('/');
    } else {
      if (window.confirm('Vuoi davvero terminare la partita?')) {
        navigate('/');
      }
    }
  };

  // ── Render ─────────────────────────────────────────
  if (state.status === 'finished') {
    return (
      <div className="local-game">
        <EndScreen
          score={state.score}
          wordsGuessed={state.wordsGuessed}
          wrongAnswers={state.wrongAnswers}
          passesUsed={3 - state.passesLeft}
          wordsHistory={state.wordsHistory || []}
          onRestart={handleRestart}
          onHome={() => navigate('/')}
        />
      </div>
    );
  }

  return (
    <div className="local-game">
      <GameHUD
        timeLeft={timeLeft}
        totalTime={TOTAL_TIME}
        score={state.score}
        passesLeft={state.passesLeft}
        onExit={handleExit}
      />

      <div className="local-game__main">
        {/* IDLE */}
        {state.status === 'idle' && (
          <div className="game-overlay u-scale-in">
            <div className="game-overlay__content">
              {isLoading ? (
                <p className="game-overlay__loading">Caricamento parole…</p>
              ) : (
                <>
                  <h1 className="game-overlay__title">Pronti?</h1>
                  <button className="start-btn" onClick={handleStartCountdown} id="btn-start">
                    INIZIA
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* COUNTDOWN */}
        {state.status === 'countdown' && (
          <div className="game-overlay">
            <span className="countdown-number" key={countdown}>
              {countdown}
            </span>
          </div>
        )}

        {/* PLAYING */}
        {state.status === 'playing' && (
          <>
            <WordDisplay word={currentWordText} animKey={state.currentWordIndex} />
            <div className="local-game__controls">
              <button className="stop-btn" onClick={handleStop} id="btn-stop">
                STOP
              </button>
            </div>
          </>
        )}

        {/* PAUSED — outcome */}
        {state.status === 'paused' && (
          <OutcomePanel
            word={currentWordText}
            passesLeft={state.passesLeft}
            onCorrect={handleCorrect}
            onWrong={handleWrong}
            onPass={handlePass}
            onReport={handleReport}
            isTimeUp={timeLeft <= 0}
          />
        )}
      </div>
    </div>
  );
};

export default LocalGame;
